import { useState } from 'react'
import { isTransportError, useOpenWindow, useWindow } from '@krp/client-runtime'
import { newUuid } from '@krp/platform-ui'
import {
  CASHIER_ACCESS_WINDOWS, OWN_CASHIERS_WINDOWS, OWN_CASHIER_NEW, OwnCashierCard, cashierAccessPatched, cashierGroupsApi, createOwnCashier, ownCashiersApi, profileOf,
  receiptsCashierCandidates, receiptsPeople, setOwnCashierCode, setOwnCashierLinks, unlockOwnCashierSignIn, updateOwnCashier, type OwnCashier, type OwnCashierLink,
} from '@krp/resource-receipts-ui'
import { useAppDispatch } from '../../app/hooks'
import { SOURCE_KINDS } from '../../composition/connectionForms'
import { useMyAccess } from '../platform/useMyAccess'

const errorText = (error: unknown) => isTransportError(error) ? error.message : error instanceof Error ? error.message : error === undefined ? null : 'Не удалось получить ответ сервера'
/** Месяц для справочника кассиров источника: служба отдаёт справочник целиком, месяц нужен договору запроса. */
function currentMonth() {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit' }).formatToParts(new Date())
  return `${parts.find((part) => part.type === 'year')?.value ?? ''}-${parts.find((part) => part.type === 'month')?.value ?? ''}-01`
}

/**
 * Карточка кассира нашей системы (RP-CU3) в модальном окне: читает кассира из того же кэша списка по id из адреса окна; «new» — создание.
 * Намерение карточки (источник для связи) — локальное состояние окна; код кассира — только в аргументе команды. Оборудование, касса и вид цены
 * по умолчанию задаются у кассира в источнике (RP-CU10): «Настройки в источнике» открывает его настройки на экране «Коды по источникам».
 */
export function OwnCashierCardBinding() {
  const dispatch = useAppDispatch()
  const access = useMyAccess()
  const windowHandle = useWindow()
  const openWindow = useOpenWindow()
  const id = windowHandle.params.id ?? ''
  const creating = id === OWN_CASHIER_NEW
  const list = receiptsPeople.useQuery()
  const canManage = access.data?.api.includes('platform:cashiers:manage') === true && list.data?.canManage === true
  const selected = creating ? undefined : list.data?.items.find((item) => item.id === id)
  const missing = !creating && list.data !== undefined && selected === undefined
  const codeMin = list.data?.codeMinLength ?? 0
  const codeMax = list.data?.codeMaxLength ?? 0
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [linkResourceId, setLinkResourceId] = useState('')
  const [linkSearch, setLinkSearch] = useState('')
  const live = selected !== undefined && !selected.archived
  const candidates = receiptsCashierCandidates.useQuery({ resourceId: linkResourceId, month: currentMonth(), timeZoneId: 'Europe/Moscow', refresh: false },
    { skip: !canManage || !live || linkResourceId === '' })

  /** Выполняет команду и обновляет кэш; ошибка сервера — дословно, конфликт версии — с подсказкой перечитать. */
  const run = async <T,>(command: () => Promise<{ ok: true; data: T } | { ok: false; error: { status?: number; message: string } }>, success: string, after?: (data: T) => void) => {
    if (busy) return false
    setError(null); setDone(null); setBusy(true)
    try {
      const result = await command()
      if (!result.ok) {
        setError(result.error.status === 409 ? `${result.error.message} Закройте и откройте карточку, затем повторите действие.` : result.error.message)
        return false
      }
      dispatch(ownCashiersApi.util.invalidateTags(['ReceiptsPeople']))
      dispatch(cashierGroupsApi.util.invalidateTags(['ReceiptsGroups', 'ReceiptsAnalytics']))
      after?.(result.data)
      setDone(success)
      return true
    } catch (caught) { setError(errorText(caught)); return false } finally { setBusy(false) }
  }
  return <OwnCashierCard creating={creating} cashier={selected} missing={missing} codeMinLength={codeMin} codeMaxLength={codeMax} canManage={canManage} busy={busy}
    error={error ?? errorText(list.error ?? candidates.error ?? access.error)} done={done} close={windowHandle.close}
    create={(input, code) => run(() => dispatch(createOwnCashier({ ...profileOf(input), code, operationId: newUuid() })), 'Кассир создан.',
      (cashier: OwnCashier) => windowHandle.replaceWith(OWN_CASHIERS_WINDOWS.card, { id: cashier.id }))}
    saveProfile={(cashier, input, archived) => run(() => dispatch(updateOwnCashier({ id: cashier.id, ...profileOf(input), archived, version: cashier.version, operationId: newUuid() })),
      archived === cashier.archived ? 'Карточка сохранена.' : archived ? 'Кассир убран в архив.' : 'Кассир возвращён из архива. Задайте ему код и связи.')}
    setCode={(cashier, code) => run(() => dispatch(setOwnCashierCode({ id: cashier.id, code, expectedVersion: cashier.codeVersion, operationId: newUuid() })), 'Код задан.')}
    unlockSignIn={(cashier) => run(() => dispatch(unlockOwnCashierSignIn({ id: cashier.id, operationId: newUuid() })), 'Блокировка входа снята: кассир может снова ввести код.')}
    linkSources={list.data?.sources ?? []} kinds={SOURCE_KINDS}
    sourcesLoad={{ loading: list.isLoading, error: list.error === undefined ? null : errorText(list.error), retry: () => void list.refetch() }}
    linkResourceId={linkResourceId} linkSearch={linkSearch} candidates={candidates.currentData?.items} candidatesLoading={candidates.isFetching}
    chooseLinkSource={(rid) => { setLinkResourceId(rid); setLinkSearch('') }} findLink={setLinkSearch}
    saveLinks={(cashier, links) => run(() => dispatch(setOwnCashierLinks({ id: cashier.id, links, version: cashier.version, operationId: newUuid() })), 'Связи сохранены.')}
    openSourceSettings={(link: OwnCashierLink) => {
      dispatch(cashierAccessPatched({ resourceId: link.resourceId, cashierKey: link.key, sourceCashierId: link.sourceCashierId ?? '', search: '' }))
      openWindow(CASHIER_ACCESS_WINDOWS.list)
    }} />
}
